import React, { memo, useEffect, useMemo } from "react";
import DynamicUserForm from "../components/form/DynamicUserForm";
import { useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCurrentUserClinicMapping } from "../context/UserClinicMappingContext";
import { useProfilePrefetch } from "../context/ProfilePrefetchContext";
import Spinner from "../components/Spinner";
import { trackEvent } from "../utils/analytics/track";
import { getSafeInternalNextPath } from "../utils/navigation/recordingModeNav";
import { mergeProfileWithClinicMapping } from "../utils/recordingMode/mergeProfileWithClinicMapping";

/**
 * FormPage
 * --------
 * Route wrapper that mounts <DynamicUserForm/> and optionally pre-fills it from:
 *  1) navigation state (highest priority)
 *  2) prefetched cached profile metadata (fallback)
 */
function FormPage() {
  const { state } = useLocation(); // { prefill } | undefined
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const mapping = useCurrentUserClinicMapping();
  const { getCachedProfile } = useProfilePrefetch();
  const returnTo = getSafeInternalNextPath(
    state?.returnTo || searchParams.get("next"),
  );

  const cachedPrefill = useMemo(() => {
    if (!user?.uid) return undefined;
    const cached = getCachedProfile(user.uid);
    if (cached?.status !== "ready") return undefined;
    return mergeProfileWithClinicMapping(cached.data, mapping);
  }, [user?.uid, getCachedProfile, mapping]);

  const mergedPrefill = useMemo(() => {
    const base = state?.prefill ?? cachedPrefill ?? {};
    return mergeProfileWithClinicMapping(base, mapping);
  }, [state?.prefill, cachedPrefill, mapping]);

  useEffect(() => {
    trackEvent("profile_form_view", {
      hasPrefillFromNav: !!state?.prefill,
      hasPrefillFromCache: !!cachedPrefill,
    });
  }, [state?.prefill, cachedPrefill]);

  if (user?.uid && !mapping.resolved) {
    return <Spinner />;
  }

  return (
    <div className="form-page w-full h-full max-w-lg">
      <DynamicUserForm
        initialData={mergedPrefill}
        returnTo={returnTo}
      />
    </div>
  );
}

export default memo(FormPage);
